/*
Markup and keyframes for the Fiestas Patrias card.

Ported from the design's build script (.superdesign/design_iterations/
fiestas-patrias/build.mjs), which is the source stakeholders reviewed. The
repeated decorations —bunting, confetti, burst particles— and the two dancers
are emitted as literal SVG/HTML strings and rendered with innerHTML: the
figures alone are a few hundred attributes, and hand-converting them to JSX
would only add a place for the port to drift from the approved design. Every
string here is built from the constants below; nothing comes from outside.

The cueca is sampled into linear keyframes at module load so the CSS engine
plays it with no script running while the modal is open.
*/

export const RED = '#d52b1e'
export const BLUE = '#0039a6'
export const WHITE = '#ffffff'
export const INK = '#1c1917'
export const MUTED = '#706d71'
export const BORDER = '#e4e1e4'
export const STRAW = '#d9a441'

// Deterministic so every render produces identical markup.
const rnd = (seed: number) => {
  let s = seed

  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296

    return s / 4294967296
  }
}

// ---------- Bunting (guirnaldas): two rope scallops with triangular flags ----------
interface Point {
  x: number
  y: number
}

const qpoint = (p0: Point, c: Point, p1: Point, t: number): Point => ({
  x: (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * c.x + t ** 2 * p1.x,
  y: (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * c.y + t ** 2 * p1.y,
})

export const bunting = (width: number, height: number, flagW: number, flagH: number, sag: number) => {
  const half = width / 2
  const scallops: [Point, Point, Point][] = [
    [
      { x: 0, y: 8 },
      { x: half / 2, y: sag },
      { x: half, y: 8 },
    ],
    [
      { x: half, y: 8 },
      { x: half + half / 2, y: sag },
      { x: width, y: 8 },
    ],
  ]
  const colors = [RED, WHITE, BLUE]
  let flags = ''
  let i = 0
  const step = flagW * 1.55

  for (const [p0, c, p1] of scallops) {
    const n = Math.floor(half / step)

    for (let k = 1; k <= n; k++) {
      const t = k / (n + 1)
      const p = qpoint(p0, c, p1, t)
      const color = colors[i % 3]
      const stroke = color === WHITE ? ` stroke="${BORDER}" stroke-width="1"` : ''
      const x = p.x.toFixed(1)
      const y = p.y.toFixed(1)
      const delay = (-(i * 0.37) % 2.4).toFixed(2)
      flags += `<polygon class="fp-flag" points="${(p.x - flagW / 2).toFixed(1)},${y} ${(p.x + flagW / 2).toFixed(1)},${y} ${x},${(p.y + flagH).toFixed(1)}" fill="${color}"${stroke} style="transform-origin: ${x}px ${y}px; animation-delay: ${delay}s;"></polygon>`
      i++
    }
  }
  const rope = `M0 8 Q${half / 2} ${sag} ${half} 8 T${width} 8`

  return `<svg class="fp-bunting" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true"><path d="${rope}" fill="none" stroke="#3b2a20" stroke-width="1.5" stroke-linecap="round"></path>${flags}</svg>`
}

// ---------- Estrella Solitaria ----------
const starPoints = (cx: number, cy: number, r: number) => {
  const pts: string[] = []

  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.4
    const a = -Math.PI / 2 + (i * Math.PI) / 5
    pts.push(`${(cx + rad * Math.cos(a)).toFixed(2)},${(cy + rad * Math.sin(a)).toFixed(2)}`)
  }

  return pts.join(' ')
}

export const star = (size: number, className = 'fp-star') => {
  const r = size / 2 - 2

  return `<svg class="${className}" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true"><polygon class="fp-star-shape" points="${starPoints(size / 2, size / 2, r)}" fill="${WHITE}"></polygon></svg>`
}

// ---------- Volantín (kite) ----------
export const kite = (fill: string, second: string, x: number, y: number, dur: number, delay: number, scale = 1) => `
<svg class="fp-kite" viewBox="-30 -40 60 150" width="${60 * scale}" height="${150 * scale}" style="left: ${x}px; top: ${y}px; animation-duration: ${dur}s; animation-delay: ${delay}s;" aria-hidden="true">
  <path d="M0 40 C -8 70, 6 90, -4 110" fill="none" stroke="#3b2a20" stroke-width="0.8" opacity="0.55"></path>
  <g class="fp-kite-tail" style="transform-origin: 0px 40px;">
    <path d="M0 40 C 6 52, -6 62, 2 74 C 8 84, -4 92, 0 104" fill="none" stroke="${fill}" stroke-width="1.6" stroke-linecap="round"></path>
    <path d="M-5 54 L5 50 L-5 46 Z" fill="${second}"></path>
    <path d="M-5 76 L5 72 L-5 68 Z" fill="${second}"></path>
    <path d="M-5 98 L5 94 L-5 90 Z" fill="${second}"></path>
  </g>
  <g class="fp-kite-body">
    <polygon points="0,-40 28,-4 0,40 -28,-4" fill="${fill}"></polygon>
    <polygon points="0,-40 28,-4 0,40" fill="${second}" opacity="0.92"></polygon>
    <polygon points="${starPoints(0, -6, 9)}" fill="${WHITE}"></polygon>
    <line x1="0" y1="-40" x2="0" y2="40" stroke="#3b2a20" stroke-width="0.8" opacity="0.5"></line>
    <line x1="-28" y1="-4" x2="28" y2="-4" stroke="#3b2a20" stroke-width="0.8" opacity="0.5"></line>
  </g>
</svg>`

// ---------- Dancers (huaso & china dancing cueca) ----------
// Each figure is drawn in a 220x300 box with the feet at y≈292.
const panuelo = (x: number, y: number, cls: string) => `
<g class="fp-panuelo ${cls}" style="transform-origin: ${x}px ${y}px;"><g transform="translate(${x} ${y}) scale(0.72) translate(${-x} ${-y})">
  <path d="M${x} ${y} C ${x + 14} ${y - 20}, ${x + 32} ${y - 12}, ${x + 44} ${y - 30} C ${x + 38} ${y - 10}, ${x + 30} ${y + 4}, ${x + 10} ${y + 8} Z" fill="${WHITE}" stroke="${BORDER}" stroke-width="1"></path>
  <path d="M${x} ${y} C ${x - 12} ${y - 8}, ${x - 24} ${y - 22}, ${x - 30} ${y - 38} C ${x - 16} ${y - 24}, ${x - 8} ${y - 14}, ${x + 2} ${y - 6} Z" fill="${WHITE}" stroke="${BORDER}" stroke-width="1" opacity="0.92"></path>
</g></g>`

export const huaso = () => `
<svg class="fp-dancer fp-huaso" viewBox="0 0 220 300" width="220" height="300" aria-hidden="true">
  <defs>
    <clipPath id="fpMantaClip"><path d="M78 118 L142 118 L164 218 L56 218 Z"></path></clipPath>
  </defs>
  <g class="fp-body">
    <g class="fp-legL" style="transform-origin: 99px 198px;">
      <rect x="90" y="196" width="18" height="78" fill="#2b2a33"></rect>
      <rect x="86" y="268" width="26" height="26" rx="6" fill="#4a2c17"></rect>
      <circle cx="86" cy="286" r="3.5" fill="none" stroke="#c9c2b6" stroke-width="1.4"></circle>
    </g>
    <g class="fp-legR" style="transform-origin: 121px 198px;">
      <rect x="112" y="196" width="18" height="78" fill="#2b2a33"></rect>
      <rect x="108" y="268" width="26" height="26" rx="6" fill="#4a2c17"></rect>
      <circle cx="134" cy="286" r="3.5" fill="none" stroke="#c9c2b6" stroke-width="1.4"></circle>
    </g>
    <path d="M84 132 C 66 150, 62 170, 70 186" fill="none" stroke="${WHITE}" stroke-width="14" stroke-linecap="round"></path>
    <circle cx="72" cy="190" r="7" fill="#e9b48e"></circle>
    <rect x="80" y="116" width="60" height="90" rx="10" fill="${WHITE}"></rect>
    <g class="fp-manta" style="transform-origin: 110px 118px;">
      <path d="M78 118 L142 118 L164 218 L56 218 Z" fill="${RED}"></path>
      <g clip-path="url(#fpMantaClip)">
        <rect x="40" y="150" width="140" height="8" fill="#1c2340"></rect>
        <rect x="40" y="162" width="140" height="3" fill="${WHITE}"></rect>
        <rect x="40" y="178" width="140" height="8" fill="#1c2340"></rect>
        <rect x="40" y="190" width="140" height="3" fill="${WHITE}"></rect>
        <rect x="40" y="206" width="140" height="6" fill="#1c2340"></rect>
      </g>
      <path d="M56 218 L164 218" stroke="#1c2340" stroke-width="6" stroke-dasharray="2 4"></path>
      <path d="M96 118 L124 118 L110 142 Z" fill="${WHITE}"></path>
    </g>
    <g class="fp-arm" style="transform-origin: 136px 130px;">
      <path d="M136 130 C 152 108, 160 84, 168 66" fill="none" stroke="${WHITE}" stroke-width="14" stroke-linecap="round"></path>
      <circle cx="170" cy="62" r="8" fill="#e9b48e"></circle>
      ${panuelo(170, 58, 'fp-panuelo-h')}
    </g>
    <circle cx="110" cy="96" r="22" fill="#e9b48e"></circle>
    <path d="M100 104 C 106 110, 114 110, 120 104" fill="none" stroke="#3b2a20" stroke-width="2.4" stroke-linecap="round"></path>
    <path d="M96 78 C 100 70, 122 70, 126 80 L 124 88 C 118 82, 102 82, 96 88 Z" fill="#3b2a20"></path>
    <path d="M92 80 C 92 100, 106 116, 110 118 C 114 116, 128 100, 128 80" fill="none" stroke="#3b2a20" stroke-width="1" opacity="0.7"></path>
    <g class="fp-hat" style="transform-origin: 110px 78px;">
      <ellipse cx="110" cy="78" rx="50" ry="9" fill="${STRAW}" stroke="#a8782a" stroke-width="1.5"></ellipse>
      <rect x="88" y="52" width="44" height="28" rx="4" fill="${STRAW}" stroke="#a8782a" stroke-width="1.5"></rect>
      <rect x="88" y="70" width="44" height="8" fill="${INK}"></rect>
    </g>
  </g>
</svg>`

export const china = () => `
<svg class="fp-dancer fp-china" viewBox="0 0 220 300" width="220" height="300" aria-hidden="true">
  <defs>
    <pattern id="fpDots" width="12" height="12" patternUnits="userSpaceOnUse">
      <circle cx="3" cy="3" r="1.6" fill="${WHITE}" opacity="0.85"></circle>
      <circle cx="9" cy="9" r="1.6" fill="${WHITE}" opacity="0.85"></circle>
    </pattern>
  </defs>
  <g class="fp-body">
    <g class="fp-legL" style="transform-origin: 100px 236px;">
      <rect x="94" y="234" width="12" height="50" fill="#d9a07b"></rect>
      <path d="M88 282 L108 282 L110 294 L84 294 Z" fill="${INK}"></path>
    </g>
    <g class="fp-legR" style="transform-origin: 120px 236px;">
      <rect x="114" y="234" width="12" height="50" fill="#d9a07b"></rect>
      <path d="M112 282 L132 282 L136 294 L110 294 Z" fill="${INK}"></path>
    </g>
    <g class="fp-skirt" style="transform-origin: 110px 172px;">
      <path d="M88 170 L132 170 C 150 196, 172 234, 184 264 C 160 276, 60 276, 36 264 C 48 234, 70 196, 88 170 Z" fill="${BLUE}"></path>
      <path d="M88 170 L132 170 C 150 196, 172 234, 184 264 C 160 276, 60 276, 36 264 C 48 234, 70 196, 88 170 Z" fill="url(#fpDots)"></path>
      <path d="M36 264 C 60 276, 160 276, 184 264 C 178 272, 168 280, 156 282 C 130 288, 90 288, 64 282 C 52 280, 42 272, 36 264 Z" fill="${WHITE}"></path>
      <path d="M92 172 L128 172 C 136 200, 144 224, 148 250 C 124 258, 96 258, 72 250 C 76 224, 84 200, 92 172 Z" fill="${WHITE}" opacity="0.94"></path>
      <path d="M92 172 L128 172" stroke="${RED}" stroke-width="6"></path>
    </g>
    <rect x="90" y="116" width="40" height="60" rx="8" fill="${BLUE}"></rect>
    <rect x="90" y="116" width="40" height="60" rx="8" fill="url(#fpDots)"></rect>
    <circle cx="88" cy="128" r="13" fill="${WHITE}"></circle>
    <circle cx="132" cy="128" r="13" fill="${WHITE}"></circle>
    <path d="M136 140 C 148 160, 154 176, 152 190" fill="none" stroke="#d9a07b" stroke-width="11" stroke-linecap="round"></path>
    <circle cx="152" cy="194" r="7" fill="#d9a07b"></circle>
    <g class="fp-arm" style="transform-origin: 84px 134px;">
      <path d="M84 134 C 70 112, 60 90, 50 70" fill="none" stroke="#d9a07b" stroke-width="11" stroke-linecap="round"></path>
      <circle cx="48" cy="66" r="7" fill="#d9a07b"></circle>
      ${panuelo(48, 62, 'fp-panuelo-c')}
    </g>
    <circle cx="110" cy="94" r="21" fill="#d9a07b"></circle>
    <path d="M101 101 C 106 107, 114 107, 119 101" fill="none" stroke="#3b2a20" stroke-width="2.2" stroke-linecap="round"></path>
    <path d="M89 92 C 88 74, 100 66, 110 66 C 122 66, 132 74, 131 92 C 126 84, 118 80, 110 80 C 102 80, 94 84, 89 92 Z" fill="#2a1a12"></path>
    <circle cx="126" cy="74" r="10" fill="#2a1a12"></circle>
    <circle cx="131" cy="70" r="5" fill="${RED}"></circle>
    <circle cx="136" cy="76" r="4" fill="${RED}"></circle>
    <circle cx="128" cy="80" r="3.5" fill="${RED}"></circle>
    <circle cx="90" cy="100" r="2.2" fill="${STRAW}"></circle>
    <circle cx="130" cy="100" r="2.2" fill="${STRAW}"></circle>
  </g>
</svg>`

// ---------- Confetti ----------
export const confetti = (count: number, seed: number, height: number) => {
  const r = rnd(seed)
  const colors = [RED, WHITE, BLUE, STRAW]
  let out = ''

  for (let i = 0; i < count; i++) {
    const left = (r() * 100).toFixed(1)
    const d = (7 + r() * 6).toFixed(2)
    const dl = (-r() * 13).toFixed(2)
    const sx = ((r() - 0.5) * 120).toFixed(0)
    const rz = (360 + r() * 720).toFixed(0)
    const w = (5 + r() * 4).toFixed(0)
    const h = (8 + r() * 6).toFixed(0)
    const color = colors[i % colors.length]
    const stroke = color === WHITE ? ` box-shadow: 0 0 0 1px ${BORDER};` : ''
    out += `<span class="fp-cf" style="left: ${left}%; --d: ${d}s; --dl: ${dl}s; --sx: ${sx}px; --fall: ${height}px;"><i style="width: ${w}px; height: ${h}px; background: ${color}; --rz: ${rz}deg;${stroke}"></i></span>`
  }

  return out
}

export const burst = (count: number, seed: number) => {
  const r = rnd(seed)
  const colors = [RED, WHITE, BLUE, STRAW]
  let out = ''

  for (let i = 0; i < count; i++) {
    const a = r() * Math.PI * 2
    const dist = 120 + r() * 220
    const bx = (Math.cos(a) * dist).toFixed(0)
    const by = (Math.sin(a) * dist * 0.8 + 90).toFixed(0)
    const color = colors[i % colors.length]
    const w = (6 + r() * 5).toFixed(0)
    const h = (8 + r() * 6).toFixed(0)
    const dl = (r() * 0.12).toFixed(2)
    const stroke = color === WHITE ? ` box-shadow: 0 0 0 1px ${BORDER};` : ''
    out += `<i class="fp-burst-p" style="--bx: ${bx}px; --by: ${by}px; width: ${w}px; height: ${h}px; background: ${color}; animation-delay: ${dl}s;${stroke}"></i>`
  }

  return out
}

// ---------- Cueca choreography ----------
// One 14 s loop, sampled into linear keyframes. Phases (fractions of the loop):
//   0.00-0.14  inicio: face each other, mark the step, pañuelos up
//   0.14-0.36  vuelta entera: circle each other and swap sides (he passes behind)
//   0.36-0.50  media luna: approach the centre and retreat
//   0.50-0.72  vuelta de regreso: circle back (he passes in front)
//   0.72-0.86  zapateo: fast alternating footwork in place
//   0.86-1.00  cierre: meet in the middle with a full turn, return home
const LOOP_S = 14
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))
const smooth = (u: number) => {
  const c = clamp(u, 0, 1)

  return c * c * (3 - 2 * c)
}
const phase = (t: number, a: number, b: number) => smooth((t - a) / (b - a))
const bump = (t: number, a: number, b: number) => (t >= a && t <= b ? Math.sin((Math.PI * (t - a)) / (b - a)) : 0)

// Beat phase: 1.5 steps/s, doubled during the zapateo; rescaled so the loop
// closes on a whole number of steps.
const tempo = (t: number) => (t >= 0.72 && t < 0.86 ? 3.0 : 1.5)
const BEAT_SAMPLES = 1400
const beatTable = (() => {
  const out = [0]
  let acc = 0

  for (let i = 1; i <= BEAT_SAMPLES; i++) {
    acc += tempo((i - 0.5) / BEAT_SAMPLES) * (LOOP_S / BEAT_SAMPLES)
    out.push(acc)
  }
  const k = Math.round(acc) / acc

  return out.map((v) => v * k * Math.PI * 2)
})()
const beat = (t: number) => beatTable[Math.round(clamp(t, 0, 1) * BEAT_SAMPLES)]

type Dancer = 'h' | 'c'

// Floor position of a dancer: angle on a shared ellipse plus a radius factor.
const floor = (t: number, who: Dancer) => {
  let theta = who === 'h' ? Math.PI : 0
  theta += Math.PI * phase(t, 0.14, 0.36)
  theta += Math.PI * phase(t, 0.5, 0.72)
  const r = 1 - 0.6 * bump(t, 0.36, 0.5) - 0.62 * bump(t, 0.86, 1.0)
  // Turns: a half turn at each side swap (timed where the two cross), a full
  // turn in the closing phase. 720° at the end reads as 0° again.
  const face = 180 * phase(t, 0.2, 0.3) + 180 * phase(t, 0.56, 0.66) + 360 * phase(t, 0.88, 0.97)

  return { theta, r, face }
}

const fmt = (v: number, d = 2) => Number(v.toFixed(d)).toString()

const keyframes = (name: string, samples: number, fn: (t: number) => string) => {
  let out = `@keyframes ${name} {`

  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    out += ` ${fmt(t * 100, 3)}% { ${fn(t === 1 ? 0.99999 : t)} }`
  }

  return out + ' }'
}

// rx/ry: ellipse radii in px; depth: how much nearer dancers grow.
const choreoCss = (rx: number, ry: number, depth: number) => {
  let css = ''

  for (const who of ['h', 'c'] as Dancer[]) {
    css += keyframes(`fpMove${who}`, 280, (t) => {
      const { theta, r, face } = floor(t, who)
      const sin = Math.sin(theta) * r
      const x = rx * r * Math.cos(theta)
      const y = ry * sin
      const s = 1 + depth * sin
      const z = sin > 0.03 ? 3 : sin < -0.03 ? 1 : 2

      return `transform: translate(${fmt(x)}px, ${fmt(y)}px) scale(${fmt(s, 3)}) rotateY(${fmt(face)}deg); z-index: ${z};`
    })
    css += keyframes(`fpShadow${who}`, 140, (t) => {
      const { theta, r } = floor(t, who)
      const sin = Math.sin(theta) * r
      const b = Math.abs(Math.sin(beat(t) + (who === 'c' ? Math.PI : 0)))

      return `transform: translate(${fmt(rx * r * Math.cos(theta))}px, ${fmt(ry * sin)}px) scale(${fmt((1 + depth * sin) * (1 - 0.18 * b), 3)}); opacity: ${fmt(0.9 - 0.35 * b)};`
    })
  }

  return css
}

// Body-part keyframes live in SVG user units, so one set serves every size.
const bodyCss = () => {
  let css = ''
  const zap = (t: number) => (t >= 0.72 && t < 0.86 ? 1 : 0)

  for (const who of ['h', 'c'] as Dancer[]) {
    const off = who === 'c' ? Math.PI : 0
    css += keyframes(`fpLegL${who}`, 280, (t) => {
      const b = beat(t) + off
      const lift = Math.max(0, Math.sin(b)) * (6 + 6 * zap(t))

      return `transform: translateY(${fmt(-lift)}px) rotate(${fmt((14 - 6 * zap(t)) * Math.sin(b))}deg);`
    })
    css += keyframes(`fpLegR${who}`, 280, (t) => {
      const b = beat(t) + off + Math.PI
      const lift = Math.max(0, Math.sin(b)) * (6 + 6 * zap(t))

      return `transform: translateY(${fmt(-lift)}px) rotate(${fmt((14 - 6 * zap(t)) * Math.sin(b))}deg);`
    })
    css += keyframes(`fpBob${who}`, 280, (t) => {
      const b = beat(t) + off

      return `transform: translateY(${fmt(-(4 + 3 * zap(t)) * Math.abs(Math.sin(b)))}px) rotate(${fmt(2.5 * Math.sin(b / 2))}deg);`
    })
    css += keyframes(`fpArm${who}`, 140, (t) => {
      const w = Math.sin(Math.PI * 2 * 5 * t + off)
      const sign = who === 'h' ? 1 : -1

      return `transform: rotate(${fmt(sign * (10 * w + 8 * bump(t, 0.86, 1.0)))}deg);`
    })
  }
  css += keyframes('fpSkirtDance', 280, (t) => {
    const b = beat(t) + Math.PI

    return `transform: skewX(${fmt(7 * Math.sin(b))}deg) scaleX(${fmt(1 + 0.2 * bump(t, 0.88, 0.97) + 0.06 * Math.abs(Math.sin(b)), 3)});`
  })

  return css
}

/** Card geometry, in the design's own pixels; the body scales it to fit. */
export const SCENE_WIDTH = 1100
export const SCENE_HEIGHT = 576

/** Every generated keyframe, built once. Static rules live in fiestasPatrias.css. */
export const CHOREOGRAPHY_CSS = bodyCss() + choreoCss(165, 40, 0.11)

/** Decorations emitted once; the body drops them in with innerHTML. */
export const SCENE = {
  bunting: bunting(SCENE_WIDTH, 96, 28, 34, 84),
  star: star(56),
  ctaStar: star(18),
  kites:
    kite(RED, '#e8483a', 500, 56, 12, -2, 0.9) +
    kite(BLUE, '#1d55c4', 880, 60, 10, -5, 1.15) +
    kite(STRAW, '#c48f2c', 1000, 236, 13, -8, 0.75),
  huaso: huaso(),
  china: china(),
  confetti: confetti(44, 7, 640),
  burst: burst(56, 11),
}
